import { Alert, Badge, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { EmptyNote } from '../../components/RecordScreen';
import { useBaseCurrency } from '../../hooks/useSeedLocation';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDate, formatMicroQty, formatMoney } from '../../lib/format';

export const STOCK_VARIANCE_PATH = '/admin/v1/reports/stock-variance';

interface VarianceRow {
  inventoryItemId: string;
  nameEn: string;
  unit: string;
  windowFrom: string;
  windowTo: string;
  measurement: string;
  openingCountOn: string | null;
  closingCountOn: string | null;
  soldUsageMicro: number;
  soldUsageCostBaseMinor: number;
  wasteMicro: number;
  wasteCostBaseMinor: number;
  preppedMicro: number;
  receivedMicro: number;
  transferNetMicro: number;
  actualUsageMicro: number | null;
  actualUsageCostBaseMinor: number | null;
  varianceMicro: number | null;
  varianceCostBaseMinor: number | null;
  recipeFaults: number;
  unpricedIngredient: boolean;
  countUnitMismatch: boolean;
  areasNotCounted: string[];
}

interface BlindSpot {
  variantId: string;
  itemNameEn: string;
  reason: string;
  componentNameEn: string | null;
  detail: string | null;
  occurrences: number;
}

interface VarianceReport {
  from: string;
  to: string;
  rows: VarianceRow[];
  blindSpots: BlindSpot[];
  countedOn: string[];
}

/**
 * What the sales say came off the shelf, against what actually went.
 *
 * The single report the owner has asked for most, and the one that was impossible to write until
 * selling a drink started taking the rum out of the bottle. It is also the report most easily
 * turned into an accusation, so this screen states the difference and stops there. There are four
 * ordinary reasons a bottle is short — a heavy hand, something spilled and never logged, a recipe
 * that says 45 ml where the bar pours 60, and a count taken in a hurry — and one that is not. The
 * screen does not choose between them. A report that accuses is a report that gets argued with
 * instead of read, and the true answer is usually the recipe.
 *
 * ## What is on the page because leaving it off would make the page a lie
 *
 * **The dates each row was actually measured over**, which are not always the dates asked for. The
 * gap is the difference two counts found; if the last count before these dates was a month back,
 * the figure covers that month and says so. That is not a caveat, it is the reading — and it is
 * also the nudge to count more often, which is the only thing that makes the number sharper.
 *
 * **"Not measured" where there is no gap to report**, never a zero. A shelf nobody counted has not
 * been found correct. Those two sentences say opposite things about a bartender.
 *
 * **Thrown away and moved, in their own columns.** Waste already comes off the shelf and off the
 * book, and a transfer between the bodega and the bar consumes nothing. Neither is a loss and
 * neither may be read as one.
 *
 * **The dishes that moved no stock at all.** They are the reason a row of perfect zeros is the most
 * dangerous thing on the page: a burger nobody has written a recipe for took a bun, a patty and a
 * slice of cheese off three shelves, and the sold column knows about none of it. Every ingredient
 * an open fault names is marked in place, and the dishes that name no ingredient are listed above
 * the table where they cannot be scrolled past.
 *
 * **The rooms a count did not reach.** The same perfect zero by a different route: a count is taken
 * of a place and this table is per item, so a bar counted exactly right prints nought beside a rum
 * with twenty cases sitting in an uncounted bodega. Named on the row, because the figure is true of
 * what was counted and only misleading about what it covers.
 */
export function StockVariance({ from, to }: { from: string; to: string }) {
  const currency = useBaseCurrency();
  const report = useQuery({
    queryKey: ['stock-variance', from, to],
    queryFn: () =>
      rawApi<VarianceReport>(
        'GET',
        `${STOCK_VARIANCE_PATH}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });

  const rows = report.data?.rows ?? [];
  const blindSpots = report.data?.blindSpots ?? [];
  const counted = report.data?.countedOn ?? [];
  const measured = rows.filter((r) => r.varianceMicro !== null);

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>What was poured, and what actually went</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm" maw={860}>
        The left column is what the recipes say your sales took off the shelf. The right is what the shelf
        actually gave up, measured by counting it. A difference has ordinary causes — a heavy hand, something
        spilled and never written down, a recipe that does not match how the drink is really made — so treat
        this as a list of things to look at, not a list of answers.
      </Text>

      {report.isError && <Alert color="red">That report could not be loaded.</Alert>}

      {!report.isLoading && counted.length === 0 && (
        <Alert color="yellow" title="Nothing in these dates was counted" mb="sm">
          A difference can only be measured between two counts of the same shelf. Nothing here was counted, so
          what you can see below is what was sold and nothing to compare it against. Count an area and run
          this again — the comparison starts from the count before it.
        </Alert>
      )}

      {blindSpots.length > 0 && (
        <Alert color="orange" title="Some dishes sold and moved no stock at all" mb="sm">
          <Text size="sm" mb="xs">
            Nothing came off the shelf for these, so nothing in the table below accounts for what they used.
            An ingredient one of them uses can show no difference at all and still be walking out of the door.
          </Text>
          <Stack gap={4}>
            {blindSpots.map((spot) => (
              <Text size="sm" key={`${spot.variantId}-${spot.reason}-${spot.componentNameEn ?? ''}`}>
                <b>{spot.itemNameEn}</b> — {enumLabel('depletionFault', spot.reason)}
                {spot.componentNameEn ? ` (${spot.componentNameEn})` : ''} · sold {spot.occurrences} times
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {!report.isLoading && rows.length === 0 && (
        <EmptyNote>Nothing moved on any shelf in these dates.</EmptyNote>
      )}

      {rows.length > 0 && (
        <Table.ScrollContainer minWidth={1040}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Item</Table.Th>
                <Table.Th ta="right">Sales say</Table.Th>
                <Table.Th ta="right">Actually went</Table.Th>
                <Table.Th ta="right">Difference</Table.Th>
                <Table.Th ta="right">Worth</Table.Th>
                <Table.Th ta="right">Thrown away</Table.Th>
                <Table.Th ta="right">Delivered</Table.Th>
                <Table.Th>What this rests on</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.inventoryItemId}>
                  <Table.Td>
                    <Text fw={500}>{row.nameEn}</Text>
                    {row.recipeFaults > 0 && (
                      <Text size="xs" c="orange.7">
                        A dish using this is selling without moving stock, so the figures beside it are short.
                      </Text>
                    )}
                    {/* The quantity columns are right and the money ones are not, which is the
                        opposite of the warning above it and must not borrow its words. */}
                    {row.unpricedIngredient && (
                      <Text size="xs" c="yellow.7">
                        Nothing has ever been bought in for this, so the amounts are right and the worth
                        beside them reads as nothing.
                      </Text>
                    )}
                    {row.countUnitMismatch && (
                      <Text size="xs" c="orange.7">
                        Counted in a different unit from the one this is kept in, so the amounts may not be in{' '}
                        {row.unit}.
                      </Text>
                    )}
                    {/* A count is taken of a place and this table is per item. A bar counted
                        exactly right prints nought beside a rum with twenty cases in the bodega. */}
                    {row.areasNotCounted.length > 0 && (
                      <Text size="xs" c="orange.7">
                        The count did not reach {row.areasNotCounted.join(', ')}, and there is stock there —
                        so this difference is what one area gave up, not the whole of it.
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">{formatMicroQty(row.soldUsageMicro, row.unit)}</Table.Td>
                  <Table.Td ta="right">
                    {row.actualUsageMicro === null ? (
                      <Text size="sm" c="dimmed">
                        Not measured
                      </Text>
                    ) : (
                      formatMicroQty(row.actualUsageMicro, row.unit)
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    {row.varianceMicro === null ? (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    ) : (
                      // Only a shelf that gave up MORE than the sales explain is coloured. A shelf
                      // that gave up less is an over-count or a delivery counted twice, and it is
                      // not the thing this report is looking for.
                      <Text size="sm" fw={600} c={row.varianceMicro > 0 ? 'red.7' : undefined}>
                        {row.varianceMicro > 0 ? '+' : ''}
                        {formatMicroQty(row.varianceMicro, row.unit)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    {row.varianceCostBaseMinor === null
                      ? '—'
                      : formatMoney(row.varianceCostBaseMinor, currency)}
                  </Table.Td>
                  <Table.Td ta="right">
                    {/* Its own column, never folded into the difference: a bottle dropped and
                        written down came off the book as well as the shelf and explains itself. */}
                    {row.wasteMicro === 0 ? '—' : formatMicroQty(row.wasteMicro, row.unit)}
                  </Table.Td>
                  <Table.Td ta="right">
                    {row.receivedMicro === 0 ? '—' : formatMicroQty(row.receivedMicro, row.unit)}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={enumColor('varianceMeasurement', row.measurement) ?? 'gray'}
                      variant="light"
                      style={{ textTransform: 'none' }}
                    >
                      {enumLabel('varianceMeasurement', row.measurement)}
                    </Badge>
                    {/* The dates a row is actually measured over, which are not always the dates
                        asked for. A range whose last previous count was in March is a March figure,
                        and printing it as this month would be the whole lie this report can tell. */}
                    <Text size="xs" c="dimmed">
                      {formatDate(row.windowFrom)} to {formatDate(row.windowTo)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {rows.length > 0 && (
        <Text size="xs" c="dimmed" mt="sm" maw={860}>
          {measured.length} of {rows.length} items on this page were counted at both ends of the dates shown;
          the rest have sales figures and nothing to compare them against. A count is only as fine as the way
          it was taken — a case counted as whole cases hides a bottle opened on the last night, either way —
          so a difference smaller than one of whatever you count in is not evidence of anything.
        </Text>
      )}
    </Paper>
  );
}
